import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Loader2, UserMinus, UserPlus, Building2, RefreshCw, AlertTriangle } from 'lucide-react';
import { dismissalApi, companiesApi } from '../lib/api';
import { useFetch } from '../lib/useFetch';
import { toast } from 'sonner';

const today = () => new Date().toISOString().slice(0, 10);

/**
 * DismissWorkerDialog — confirms dismissal of a worker (consultants only).
 */
export const DismissWorkerDialog = ({ worker, open, onOpenChange, onDone }) => {
  const [reason, setReason] = useState('');
  const [date, setDate] = useState(today());
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) { setReason(''); setDate(today()); } }, [open]);

  const submit = async () => {
    if (!worker?.id) return;
    setBusy(true);
    try {
      await dismissalApi.dismissWorker(worker.id, { reason, date });
      toast.success('Lavoratore dismesso');
      onDone?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore dismissione');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="dismiss-worker-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base"><UserMinus size={18} className="text-red-600" /> Dismetti lavoratore</DialogTitle>
          <DialogDescription>
            {worker && (() => {
              const fullName = `${worker.firstName || ''} ${worker.lastName || ''}`.trim() || worker.fiscalCode || worker.id;
              return <>Stai dismettendo <strong>{fullName}</strong>. Le sue scadenze e i suoi file restano collegati e potranno seguirlo nella prossima azienda.</>;
            })()}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <label className="text-xs font-semibold text-slate-700">Data dismissione</label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="dismiss-worker-date" className="mt-1" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-700">Motivo</label>
            <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Es. fine contratto stagionale, dimissioni…" data-testid="dismiss-worker-reason" className="mt-1" />
          </div>
        </div>
        <DialogFooter>
          <button onClick={() => onOpenChange(false)} className="px-3 py-2 rounded-lg border border-slate-200 hover:bg-slate-50 text-xs font-semibold">Annulla</button>
          <button onClick={submit} disabled={busy} data-testid="dismiss-worker-submit" className="ro-hide px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-xs font-bold flex items-center gap-1">
            {busy ? <Loader2 size={13} className="animate-spin" /> : <UserMinus size={13} />} Conferma dismissione
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/**
 * RehireWorkerDialog — select destination company (active only) and confirm.
 */
export const RehireWorkerDialog = ({ worker, open, onOpenChange, onDone }) => {
  const [companyId, setCompanyId] = useState('');
  const [date, setDate] = useState(today());
  const [mansione, setMansione] = useState('');
  const [busy, setBusy] = useState(false);

  const { data: companies } = useFetch(() => companiesApi.list(), [open]);

  useEffect(() => {
    if (open) {
      setCompanyId(worker?.companyId || '');
      setDate(today());
      setMansione(worker?.mansione || '');
    }
  }, [open, worker]);

  const activeCompanies = useMemo(() => (companies || []).filter((c) => c.id && c.status !== 'dismesso'), [companies]);

  const submit = async () => {
    if (!worker?.id || !companyId) { toast.warning('Seleziona un\'azienda'); return; }
    setBusy(true);
    try {
      const res = await dismissalApi.rehireWorker(worker.id, { companyId, date, mansione: mansione || undefined });
      const migrated = res.data?.migrated || {};
      const total = Object.values(migrated).reduce((a, b) => a + b, 0);
      toast.success(total > 0 ? `Riassunto. ${total} scadenze trasferite alla nuova azienda` : 'Riassunto');
      onDone?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore riassunzione');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="rehire-worker-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base"><UserPlus size={18} className="text-emerald-600" /> Riassumi lavoratore</DialogTitle>
          <DialogDescription>
            {worker && (() => {
              const fullName = `${worker.firstName || ''} ${worker.lastName || ''}`.trim() || worker.fiscalCode || worker.id;
              return <>Riassumi <strong>{fullName}</strong>. Tutte le scadenze (formazione, visite, DPI, HACCP, sicurezza) e i file caricati seguiranno il lavoratore nella nuova azienda.</>;
            })()}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <label className="text-xs font-semibold text-slate-700">Azienda destinazione</label>
            <Select value={companyId} onValueChange={setCompanyId}>
              <SelectTrigger data-testid="rehire-worker-company" className="mt-1"><SelectValue placeholder="Seleziona azienda…" /></SelectTrigger>
              <SelectContent className="max-h-80">
                {activeCompanies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="text-[11px] text-slate-500 mt-1">Sono elencate solo le aziende attive (non dismesse).</p>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-700">Data riassunzione</label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="rehire-worker-date" className="mt-1" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-700">Mansione (opzionale)</label>
            <Input value={mansione} onChange={(e) => setMansione(e.target.value)} placeholder="Es. cameriere, magazziniere…" data-testid="rehire-worker-mansione" className="mt-1" />
          </div>
        </div>
        <DialogFooter>
          <button onClick={() => onOpenChange(false)} className="px-3 py-2 rounded-lg border border-slate-200 hover:bg-slate-50 text-xs font-semibold">Annulla</button>
          <button onClick={submit} disabled={busy} data-testid="rehire-worker-submit" className="ro-hide px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-xs font-bold flex items-center gap-1">
            {busy ? <Loader2 size={13} className="animate-spin" /> : <UserPlus size={13} />} Conferma riassunzione
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/**
 * DismissCompanyDialog — confirms company contract termination.
 */
export const DismissCompanyDialog = ({ company, open, onOpenChange, onDone }) => {
  const [reason, setReason] = useState('');
  const [date, setDate] = useState(today());
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) { setReason(''); setDate(today()); } }, [open]);

  const submit = async () => {
    if (!company?.id) return;
    setBusy(true);
    try {
      await dismissalApi.dismissCompany(company.id, { reason, date });
      toast.success('Azienda spostata in archivio');
      onDone?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore dismissione');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="dismiss-company-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base"><Building2 size={18} className="text-red-600" /> Dismetti azienda</DialogTitle>
          <DialogDescription>
            {company && <>Stai dismettendo <strong>{company.name}</strong>. I dati, scadenze e file vengono conservati e saranno disponibili in Archivio. Gli alert email automatici verranno sospesi.</>}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <label className="text-xs font-semibold text-slate-700">Data dismissione</label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="dismiss-company-date" className="mt-1" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-700">Motivo (es. recesso contratto)</label>
            <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="dismiss-company-reason" className="mt-1" />
          </div>
          <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2 flex items-start gap-1">
            <AlertTriangle size={13} className="mt-[1px] flex-shrink-0" />
            <span>L'azienda non comparirà più negli elenchi standard. Potrai riattivarla in qualsiasi momento dall'Archivio.</span>
          </div>
        </div>
        <DialogFooter>
          <button onClick={() => onOpenChange(false)} className="px-3 py-2 rounded-lg border border-slate-200 hover:bg-slate-50 text-xs font-semibold">Annulla</button>
          <button onClick={submit} disabled={busy} data-testid="dismiss-company-submit" className="ro-hide px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-xs font-bold flex items-center gap-1">
            {busy ? <Loader2 size={13} className="animate-spin" /> : <Building2 size={13} />} Conferma dismissione
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/**
 * ReactivateCompanyDialog — restore a dismissed company.
 */
export const ReactivateCompanyDialog = ({ company, open, onOpenChange, onDone }) => {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) setNote(''); }, [open]);

  const submit = async () => {
    if (!company?.id) return;
    setBusy(true);
    try {
      await dismissalApi.reactivateCompany(company.id, { note });
      toast.success('Azienda riattivata');
      onDone?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore riattivazione');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="reactivate-company-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base"><RefreshCw size={18} className="text-emerald-600" /> Riattiva azienda</DialogTitle>
          <DialogDescription>
            {company && <>Riattivi <strong>{company.name}</strong>. Tutti i dati storici (workers, scadenze, file, audit log) sono già presenti e restano intatti.</>}
          </DialogDescription>
        </DialogHeader>
        <div className="py-2">
          <label className="text-xs font-semibold text-slate-700">Nota (opzionale)</label>
          <Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Es. nuovo contratto firmato il…" data-testid="reactivate-company-note" className="mt-1" />
        </div>
        <DialogFooter>
          <button onClick={() => onOpenChange(false)} className="px-3 py-2 rounded-lg border border-slate-200 hover:bg-slate-50 text-xs font-semibold">Annulla</button>
          <button onClick={submit} disabled={busy} data-testid="reactivate-company-submit" className="ro-hide px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-xs font-bold flex items-center gap-1">
            {busy ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />} Conferma riattivazione
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
